import React, { useState, useEffect, useRef, createContext, useContext } from "react";

// Create Tab Context for activeTab state management
const TabContext = createContext();

// Custom hook to use Tab Context
export const useTabContext = () => {
  const context = useContext(TabContext);
  if (!context) {
    return null; // Allow TabPanels to work without TabWrapper (standalone mode)
  }
  return context;
};

// High-level TabWrapper component that automatically generates navigation from descendant TabPanels
export function TabWrapper({ defaultTab, customStyles, navId, contentId, children }) {
  const tabStyles = customStyles || defaultTabStyles;
  const contentRef = useRef(null);
  const [tabs, setTabs] = useState([]);
  const [activeTab, setActiveTab] = useState(defaultTab);

  // Use DOM querySelectorAll to find all descendant TabPanels for navigation generation
  useEffect(() => {
    if (contentRef.current) {
      // Find all elements with data-tab-panel attribute at any depth
      const tabPanelElements = contentRef.current.querySelectorAll("[data-tab-panel]");

      const discoveredTabs = Array.from(tabPanelElements).map((element) => ({
        id: element.dataset.tabId,
        label: element.dataset.tabLabel,
      }));

      console.log("TabWrapper useEffect setTabs", discoveredTabs);
      setTabs(discoveredTabs);

      // Set default active tab if not already set
      if (!defaultTab && discoveredTabs.length > 0) {
        setActiveTab(discoveredTabs[0].id);
      }
    }
  }, [children, defaultTab]);

  return (
    <>
      {/* Auto-generated Bootstrap Nav Tabs */}
      <ul className="nav nav-tabs" id={navId} role="tablist" style={tabStyles.navTabs}>
        {tabs.map((tab) => (
          <TabNavItem
            key={tab.id}
            id={tab.id}
            label={tab.label}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            customTabStyles={tabStyles}
          />
        ))}
      </ul>

      {/* Tab Content - wrapped in TabContext for explicit activeTab management */}
      <TabContext.Provider value={{ activeTab, tabStyles }}>
        <div className="tab-content" id={contentId} style={tabStyles.tabContent} ref={contentRef}>
          {children}
        </div>
      </TabContext.Provider>
    </>
  );
}

// Reusable Tab Navigation Item Component
export function TabNavItem({ id, label, activeTab, setActiveTab, customTabStyles }) {
  return (
    <li className="nav-item" role="presentation">
      <a
        className={`nav-link ${activeTab === id ? "active" : ""}`}
        id={`${id}-tab`}
        data-toggle="tab"
        href={`#${id}`}
        role="tab"
        aria-controls={id}
        aria-selected={activeTab === id}
        onClick={(e) => {
          e.preventDefault();
          setActiveTab(id);
        }}
        style={{
          ...customTabStyles.navLink,
          ...(activeTab === id ? customTabStyles.navLinkActive : {}),
        }}
        onMouseEnter={(e) => {
          if (activeTab !== id) {
            Object.assign(e.target.style, customTabStyles.navLinkHover);
          }
        }}
        onMouseLeave={(e) => {
          if (activeTab !== id) {
            Object.assign(e.target.style, customTabStyles.navLink);
          }
        }}
      >
        {label}
      </a>
    </li>
  );
}

// Enhanced Tab Panel Component that uses TabContext for explicit class management
export function TabPanel({ tabId, label, id, customTabStyles, children }) {
  // Use tabId if provided (for TabWrapper), otherwise fall back to id (for standalone usage)
  const panelId = tabId || id;

  // Get activeTab from TabContext (if within TabWrapper) or use props (standalone)
  const tabContext = useTabContext();
  const activeTab = tabContext?.activeTab;
  const contextStyles = tabContext?.tabStyles;

  // Determine if this panel is active
  const isActive = activeTab === panelId;

  // Build class names explicitly - override Bootstrap's default behavior
  const classNames = ["tab-pane", "fade", ...(isActive ? ["show", "active"] : [])].join(" ");

  return (
    <div
      className={classNames}
      id={panelId}
      role="tabpanel"
      aria-labelledby={`${panelId}-tab`}
      style={contextStyles?.tabPane || customTabStyles?.tabPane || defaultTabStyles.tabPane}
      data-tab-panel="true"
      data-tab-id={panelId}
      data-tab-label={label}
    >
      <div className="mt-3">{children}</div>
    </div>
  );
}

// Default tab styles that can be imported and customized
export const defaultTabStyles = {
  navTabs: {
    borderBottom: "none",
    marginBottom: "0",
  },
  navLink: {
    border: "none",
    borderRadius: "0",
    marginRight: "2px",
    color: "#495057",
    backgroundColor: "transparent",
    padding: "0.5rem 1rem",
    textDecoration: "none",
    borderBottom: "2px solid transparent",
    transition: "border-bottom-color 0.15s ease-in-out",
  },
  navLinkHover: {
    borderBottom: "2px solid #007bff",
    color: "#495057",
    textDecoration: "none",
  },
  navLinkActive: {
    border: "none",
    backgroundColor: "transparent",
    color: "#495057",
    borderBottom: "2px solid #007bff",
  },
  tabContent: {
    border: "none",
    backgroundColor: "#fff",
    padding: "1rem 0",
  },
  tabPane: {
    border: "none",
    borderRadius: "0",
    padding: "0",
  },
};
